import { IBulkFixOutcome } from './bulk-fix-diff';

/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1959 */

export interface IParallelFixItem {
  id?: number;
  label?: string;
}

/** What the engine needs from the editor. Everything touching the app lives behind this seam so it can be tested. */
export interface IParallelFixDeps<Q extends IParallelFixItem> {
  /** the item open in the editor right now (read again whenever it matters, it can change mid-run) */
  getCurrent(): Q | null | undefined;
  isLocked(question: Q): boolean;
  /**
   * hydrate the item's saved content in place without selecting it. May answer with a reason to leave the item
   * alone (e.g. another author is editing it right now); the item is then skipped without being fixed.
   */
  load(question: Q): Promise<void | { skipReason?: string }>;
  /**
   * asked just before an item is saved: has anything happened to it since it was loaded (saved by someone else,
   * opened by another author)? A reason skips the save so their work is never overwritten.
   */
  recheck?(question: Q): Promise<string | null | undefined | void>;
  /** serialised saved state, taken right after load, used as the "previous" state of the save */
  serialize(question: Q): string;
  /** apply the fix and decide: nothing changed / changed / blocked (with reason, the change is reverted) */
  fix(question: Q): { isChanged: boolean, blockedReason?: string };
  /** persist the item in the background; resolves null when saving is not allowed */
  save(question: Q, previousState: string): Promise<any>;
  /** server-side validation of the saved item */
  validate(question: Q): Promise<boolean | void>;
  /** author-facing option problems after the fix */
  review(question: Q): string[];
  /** the normal path for the item open in the editor; it records its own outcome and must itself refuse if the item is no longer open */
  saveOpenItem(question: Q): Promise<IBulkFixOutcome>;
  /** called after each item finishes, for progress display */
  onProgress?(done: number, total: number): void;
}

export interface IParallelFixOptions {
  concurrency: number;
}

/**
 * Fixes many items at once without the open/save cycle, with guards so nothing is ever written to the wrong item:
 *  - each item id is processed once (duplicates in the list are dropped);
 *  - after loading, the object must still be the item it was asked to fix (id unchanged), before any save;
 *  - an item that became the open item while the run was going is not touched in the background;
 *  - the open item is fixed through the normal path only if it is still the open item at that moment,
 *    otherwise it is fixed like the others;
 *  - an item locked, or being edited by another author, is left alone (checked on load, and the lock again after it);
 *  - just before a save the item is re-checked, so one saved or opened by someone else since it was loaded is skipped;
 *  - one failing item never stops or affects the others.
 */
export const runParallelBulkFix = async <Q extends IParallelFixItem>(
  questions: Q[],
  deps: IParallelFixDeps<Q>,
  opts: IParallelFixOptions,
): Promise<IBulkFixOutcome> => {
  const outcome: IBulkFixOutcome = { saved: [], blocked: [], skipped: [], review: [] };
  const seen = new Set<number>();
  const unique = (questions ?? []).filter(q => {
    if (!q || q.id == null || seen.has(q.id)) { return false; }
    seen.add(q.id);
    return true;
  });
  const total = unique.length;
  let done = 0;
  const finish = () => { done++; deps.onProgress?.(done, total); };
  const isOpenNow = (q: Q) => {
    const open = deps.getCurrent();
    return !!open && (open === q || open.id === q.id);
  };

  const fixInBackground = async (question: Q) => {
    const id = question.id;
    const label = question.label;
    try {
      if (isOpenNow(question)) {
        outcome.skipped.push({ label, reason: 'opened in the editor during the fix' });
        return;
      }
      if (deps.isLocked(question)) {
        outcome.skipped.push({ label, reason: 'locked' });
        return;
      }
      const loaded = await deps.load(question);
      if (question.id !== id) {
        outcome.skipped.push({ label, reason: 'loaded a different item, nothing was saved' });
        return;
      }
      if (loaded && loaded.skipReason) {
        outcome.skipped.push({ label, reason: loaded.skipReason });
        return;
      }
      // the item list can be stale: a lock set since it loaded only shows on the item's own content
      if (deps.isLocked(question)) {
        outcome.skipped.push({ label, reason: 'locked' });
        return;
      }
      if (isOpenNow(question)) { // opened by the author while it was loading: they own it now
        outcome.skipped.push({ label, reason: 'opened in the editor during the fix' });
        return;
      }
      const previousState = deps.serialize(question);
      const result = deps.fix(question);
      if (result.blockedReason) {
        outcome.blocked.push({ label, reason: result.blockedReason });
        return;
      }
      if (question.id !== id) {
        outcome.skipped.push({ label, reason: 'item identity changed during the fix, nothing was saved' });
        return;
      }
      if (result.isChanged) {
        if (isOpenNow(question)) {
          outcome.skipped.push({ label, reason: 'opened in the editor during the fix' });
          return;
        }
        const staleReason = deps.recheck ? await deps.recheck(question) : null;
        if (staleReason) {
          outcome.skipped.push({ label, reason: staleReason });
          return;
        }
        if (question.id !== id || isOpenNow(question)) { // the re-check is a round trip: look again
          outcome.skipped.push({ label, reason: 'opened in the editor during the fix' });
          return;
        }
        const res = await deps.save(question, previousState);
        if (res === null) {
          outcome.skipped.push({ label, reason: 'read-only or locked' });
          return;
        }
      }
      outcome.saved.push({ label });
      try {
        const didValidate = await deps.validate(question);
        if (didValidate === false) {
          (outcome.unvalidated ||= []).push({ label, reason: 'the score matrix is not confirmed and up to date, or the item has too many inputs to validate' });
        } else {
          (outcome.validated ||= []).push({ label });
        }
      } catch (e) {
        (outcome.unvalidated ||= []).push({ label, reason: `validation could not run (${(e as any)?.message ?? e})` });
      }
      deps.review(question).forEach(reason => outcome.review.push({ label, reason }));
    }
    catch (e) {
      outcome.skipped.push({ label, reason: `${(e as any)?.message ?? e}` });
    }
    finally {
      finish();
    }
  };

  const queue = unique.slice();
  const openAtStart = deps.getCurrent();
  const deferredOpen: Q[] = [];
  const worker = async () => {
    while (queue.length) {
      const question = queue.shift();
      if (openAtStart && (question === openAtStart || question.id === openAtStart.id)) {
        deferredOpen.push(question); // the open item goes last, through the normal path
        continue;
      }
      await fixInBackground(question);
    }
  };
  const workerCount = Math.max(1, Math.min(opts.concurrency, queue.length));
  await Promise.all(Array.from({ length: workerCount }, worker));

  for (const question of deferredOpen) {
    if (isOpenNow(question)) {
      try {
        const own = await deps.saveOpenItem(question);
        outcome.saved.push(...own.saved);
        outcome.blocked.push(...own.blocked);
        outcome.skipped.push(...own.skipped);
        outcome.review.push(...(own.review ?? []));
      } catch (e) {
        outcome.skipped.push({ label: question.label, reason: `${(e as any)?.message ?? e}` });
      }
      finish();
    } else {
      // the author moved on, so it is no longer the open item: fix it in the background like the rest
      await fixInBackground(question);
    }
  }
  return outcome;
};
